/**
 * CanvasDoc — lightweight jsPDF-compatible canvas adapter for A4 portrait.
 * Coordinates & sizes use millimeters (mm) like jsPDF; font sizes use points (pt).
 * Provides a minimal subset of the jsPDF API for drawing text, shapes, images,
 * pagination, and saving as a single-page JPEG (stitched when multiple pages).
 */
class CanvasDoc {
    /**
     * Construct a CanvasDoc drawing surface.
     * Initializes an A4-sized canvas (portrait), white background, and default styles.
     * @param {Object} [opts]
     * @param {number} [opts.dpi=150] - Device pixels per inch (used for mm↔px and pt↔px).
     * @param {number} [opts.pageWidthMM=210] - Page width in mm.
     * @param {number} [opts.pageHeightMM=297] - Page height in mm.
     */
    constructor({ dpi = 150, pageWidthMM = 210, pageHeightMM = 297 } = {}) {
        this.dpi = dpi
        this.ppmm = dpi / 25.4 // pixels per millimeter
        this.pageWmm = pageWidthMM
        this.pageHmm = pageHeightMM
        this.offsetYpx = 0 // grows when addPage() is called

        // canvas sized to one page initially
        this.canvas = document.createElement('canvas')
        this.ctx = this.canvas.getContext('2d')
        this._resizeCanvas(pageWidthMM, pageHeightMM)

        this._paintWhite(0, 0, this.canvas.width, this.canvas.height)

        // track async image draws
        this._pendingImages = []

        // defaults
        this.fontFamily = 'NotoSans'
        this.fontStyle = 'normal' // 'normal' | 'bold' | 'thin' (maps to normal)
        this.fontSizePt = 12
        this.fillStyle = 'rgb(0,0,0)'
        this.strokeStyle = 'rgb(0,0,0)'
        this.lineWidthPx = 1
    }

    /**
     * Paint a solid white rectangle (internal helper).
     * Preserves previous fill style.
     * @param {number} x - Left in pixels.
     * @param {number} y - Top in pixels.
     * @param {number} w - Width in pixels.
     * @param {number} h - Height in pixels.
     * @private
     */
    _paintWhite(x, y, w, h) {
        const prevFill = this.ctx.fillStyle
        this.ctx.fillStyle = '#ffffff'
        this.ctx.fillRect(x, y, w, h)
        this.ctx.fillStyle = prevFill
    }

    /**
     * jsPDF-compatible no-op: add a file into the Virtual File System (ignored here).
     * Provided for API compatibility with code that calls it.
     */
    addFileToVFS() {}

    /**
     * jsPDF-compatible no-op: register a font (ignored here).
     * Provided for API compatibility with code that calls it.
     */
    addFont() {}

    /**
     * Set the active font family and style.
     * Maps 'thin' to 'normal' since Canvas has no thin weight by default.
     * @param {string} [family='NotoSans']
     * @param {'normal'|'bold'|'thin'} [style='normal']
     */
    setFont(family = 'NotoSans', style = 'normal') {
        this.fontFamily = family || 'NotoSans'
        // map 'thin' to normal for Canvas
        this.fontStyle = style === 'bold' ? 'bold' : 'normal'
    }

    /**
     * Set the active font size in points.
     * @param {number} pt - Font size (pt).
     */
    setFontSize(pt) {
        this.fontSizePt = pt
    }

    /**
     * Set the current text fill color (RGB 0–255).
     * @param {number} [r=0]
     * @param {number} [g=0]
     * @param {number} [b=0]
     */
    setTextColor(r = 0, g = 0, b = 0) {
        this.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`
    }

    /**
     * Set the current fill color for filled shapes (RGB 0–255).
     * @param {number} [r=0]
     * @param {number} [g=0]
     * @param {number} [b=0]
     */
    setFillColor(r = 0, g = 0, b = 0) {
        this.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`
    }

    /**
     * Set the current stroke color for lines/rect strokes (RGB 0–255).
     * @param {number} [r=0]
     * @param {number} [g=0]
     * @param {number} [b=0]
     */
    setDrawColor(r = 0, g = 0, b = 0) {
        this.strokeStyle = `rgb(${r | 0},${g | 0},${b | 0})`
    }

    /**
     * Set stroke width in millimeters; converted to device pixels internally.
     * @param {number} wmm - Line width in mm.
     */
    setLineWidth(wmm) {
        this.lineWidthPx = Math.max(1, wmm * this.ppmm)
    }

    /**
     * Measure text width in millimeters using current font family/style/size.
     * Mirrors jsPDF.getTextWidth semantics.
     * @param {string} text
     * @returns {number} Width in mm.
     */
    getTextWidth(text) {
        const pxSize = this._ptToPx(this.fontSizePt)
        this._applyFont(pxSize)
        const wpx = this.ctx.measureText(String(text || '')).width
        return wpx / this.ppmm
    }

    /**
     * Draw text at (x,y) in millimeters (baseline-aligned).
     * Uses current font, size, and text color.
     * @param {number} xmm - X position in mm.
     * @param {number} ymm - Y position in mm.
     * @param {string} str - Text to render.
     */
    text(xmm, ymm, str) {
        const pxSize = this._ptToPx(this.fontSizePt)
        this._applyFont(pxSize)
        this.ctx.fillStyle = this.fillStyle
        this.ctx.textBaseline = 'alphabetic'
        this.ctx.fillText(String(str || ''), this._mm(xmm), this._mm(ymm) + this.offsetYpx)
    }

    /**
     * Draw a rectangle (filled or stroked) in millimeters.
     * Mode 'F' fills; otherwise strokes.
     * @param {number} xmm - Left in mm.
     * @param {number} ymm - Top in mm.
     * @param {number} wmm - Width in mm.
     * @param {number} hmm - Height in mm.
     * @param {'F'|undefined} mode
     */
    rect(xmm, ymm, wmm, hmm, mode) {
        const x = this._mm(xmm),
            y = this._mm(ymm) + this.offsetYpx
        const w = this._mm(wmm),
            h = this._mm(hmm)
        this.ctx.lineWidth = this.lineWidthPx
        if (mode === 'F') {
            this.ctx.fillStyle = this.fillStyle
            this.ctx.fillRect(x, y, w, h)
        } else {
            this.ctx.strokeStyle = this.strokeStyle
            this.ctx.strokeRect(x, y, w, h)
        }
    }

    /**
     * Draw a straight line between two points in millimeters.
     * Uses current stroke color and line width.
     * @param {number} x1mm
     * @param {number} y1mm
     * @param {number} x2mm
     * @param {number} y2mm
     */
    line(x1mm, y1mm, x2mm, y2mm) {
        this.ctx.lineWidth = this.lineWidthPx
        this.ctx.strokeStyle = this.strokeStyle
        this.ctx.beginPath()
        this.ctx.moveTo(this._mm(x1mm), this._mm(y1mm) + this.offsetYpx)
        this.ctx.lineTo(this._mm(x2mm), this._mm(y2mm) + this.offsetYpx)
        this.ctx.stroke()
    }

    /**
     * Draw an image (data URL) at (x,y) in mm, scaled to (w,h) in mm.
     * Loads asynchronously; returns a promise that resolves when drawn (fail-soft on error).
     * @param {string} dataURL - data:image/... URL.
     * @param {number} xmm
     * @param {number} ymm
     * @param {number} wmm
     * @param {number} hmm
     * @returns {Promise<void>}
     */
    addImage(dataURL, xmm, ymm, wmm, hmm) {
        const img = new Image()

        const p = new Promise((resolve) => {
            img.onload = () => {
                this.ctx.drawImage(img, this._mm(xmm), this._mm(ymm) + this.offsetYpx, this._mm(wmm), this._mm(hmm))
                resolve()
            }
            img.onerror = () => resolve() // fail-soft: don't block save
        })

        img.src = dataURL
        this._pendingImages.push(p)
        return p
    }

    /**
     * Draw an image (data URL) tiled to fill (wmm, hmm) at (xmm, ymm) in mm.
     * Loads asynchronously; returns a promise that resolves when drawn (fail-soft on error).
     * @param {string} dataURL - data:image/... URL.
     * @param {number} xmm
     * @param {number} ymm
     * @param {number} wmm
     * @param {number} hmm
     * @returns {Promise<void>}
     */
    addImageTiled(dataURL, xmm, ymm, wmm, hmm) {
        const img = new Image()

        const p = new Promise((resolve) => {
            img.onload = () => {
                // Create a repeating pattern
                const pattern = this.ctx.createPattern(img, 'repeat')

                if (pattern) {
                    this.ctx.save()
                    this.ctx.translate(this._mm(xmm), this._mm(ymm) + this.offsetYpx)
                    this.ctx.fillStyle = pattern
                    this.ctx.fillRect(0, 0, this._mm(wmm), this._mm(hmm))
                    this.ctx.restore()
                }

                resolve()
            }
            img.onerror = () => resolve() // fail-soft: don't block save
        })

        img.src = dataURL
        this._pendingImages.push(p)
        return p
    }

    /**
     * Wait for all pending async image draws to complete.
     * Used before saving to ensure the canvas contains all images.
     * @returns {Promise<void>}
     * @private
     */
    async _flushImages() {
        if (this._pendingImages.length) {
            await Promise.all(this._pendingImages)
            this._pendingImages = []
        }
    }

    /**
     * Append a new A4 page below the current one and continue drawing there.
     * Internally resizes the backing canvas, preserves prior content, paints new area white,
     * and updates the Y offset so subsequent draws land on the next page.
     */
    addPage() {
        const addPx = this._mm(this.pageHmm)
        const old = this.canvas
        const newCanvas = document.createElement('canvas')
        newCanvas.width = old.width
        newCanvas.height = old.height + addPx
        const nctx = newCanvas.getContext('2d')

        // keep what was already drawn
        nctx.drawImage(old, 0, 0)

        // paint the newly added page area white
        nctx.save()
        nctx.fillStyle = '#ffffff'
        nctx.fillRect(0, old.height, newCanvas.width, addPx)
        nctx.restore()

        this.canvas = newCanvas
        this.ctx = nctx
        this.offsetYpx += addPx
    }

    /**
     * Save the stitched canvas as a single JPEG file.
     * Awaits any pending image draws, then triggers a download.
     * @param {string} [filename='document.jpg'] - Suggested filename.
     * @returns {Promise<void>}
     */
    async save(filename = 'document.jpg') {
        await this._flushImages() // ensure all images are drawn
        const url = this.canvas.toDataURL('image/jpeg', 0.92)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
    }

    /**
     * Convert points (pt) to pixels based on current DPI.
     * @param {number} pt
     * @returns {number} Pixels.
     * @private
     */
    _ptToPx(pt) {
        return (pt * this.dpi) / 72
    }

    /**
     * Convert millimeters (mm) to pixels based on current DPI.
     * @param {number} v - Millimeters.
     * @returns {number} Pixels.
     * @private
     */
    _mm(v) {
        return v * this.ppmm
    }

    /**
     * Apply the current font style/family/size to the Canvas 2D context.
     * @param {number} px - Font size in pixels.
     * @private
     */
    _applyFont(px) {
        const weight = this.fontStyle === 'bold' ? 'bold' : 'normal'
        // Canvas font string: "<weight> <size>px <family>"
        this.ctx.font = `${weight} ${px}px ${this.fontFamily}, Arial, sans-serif`
    }

    /**
     * Resize the underlying canvas to a given page size in millimeters.
     * @param {number} wmm - Width in mm.
     * @param {number} hmm - Height in mm.
     * @private
     */
    _resizeCanvas(wmm, hmm) {
        this.canvas.width = this._mm(wmm) | 0
        this.canvas.height = this._mm(hmm) | 0
    }
}
export default CanvasDoc
